import { Clickable } from '@carvana/merch-components';
import cx from 'classnames';
import { useContext } from 'react';
import { isProd } from '~/constants/environment';
import MoreDetailsModalSections from '~/constants/MoreDetailsModalSections';
import { useHasSoftPull } from '~/contexts/FinanceAndTradeContext';
import ModalContext from '~/contexts/ModalContext';
import { useShouldUpdateTerms } from '~/contexts/PricingContext';
import SearchContext from '~/contexts/SearchContext';
import { useTileDataContext } from '~/contexts/TileDataContext';
import { useHasActiveTradeApplied } from '~/hooks/selectors';
import InfoIcon from '~/icons/InfoIcon';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import GreyCheckMarkIcon from './GreyCheckMarkIcon';
import MonthlyPayment from './MonthlyPayment';
import PricingErrorTooltip from './PricingErrorTooltip';
import LoadingTerms from '../../LoadingTerms';
import { useHasFinanceFailed, usePriceTerms } from '../hooks';
import useSendOpenModalEvent, { OpenSource } from '../useSendOpenModalEvent';

export default function Terms(): JSX.Element | null {
  const { pricingErrorMessage, totalCashDue = 0 } = usePriceTerms();
  const hasValidSoftPull = useHasSoftPull();
  const { tileData } = useTileDataContext();
  const { setMoreDetailsModalOpen } = useContext(ModalContext);
  const sendOpenEvent = useSendOpenModalEvent();
  const hasIndividualFinanceFailed = useHasFinanceFailed();
  const hasTradeIn = useHasActiveTradeApplied();
  const { showFinance } = useContext(SearchContext);
  const termsAreUpdating = useShouldUpdateTerms();
  const hidden = !showFinance;

  if (hasIndividualFinanceFailed) {
    return null;
  }

  if (pricingErrorMessage && !isProd) {
    return (
      <div
        data-qa="base-terms"
        className={cx('flex mb-20 mr-16 self-end text-sm z-100', 'bold', {
          hidden
        })}
      >
        <PricingErrorTooltip pricingErrorMessage={pricingErrorMessage} />
      </div>
    );
  }

  return (
    <div
      data-qa="base-terms"
      className={cx('flex flex-col items-end justify-end text-grey-5 text-sm', {
        hidden
      })}
    >
      {termsAreUpdating ? (
        <LoadingTerms />
      ) : (
        <>
          {hasTradeIn && (
            <div className="flex gap-4 items-center">
              <GreyCheckMarkIcon /> Trade applied
            </div>
          )}

          <MonthlyPayment />

          <div className="flex gap-4">
            <div
              data-down-payment={totalCashDue}
              className={
                totalCashDue === 0 && hasValidSoftPull
                  ? 'text-green-2 font-bold'
                  : ''
              }
              data-qa="down-payment"
            >
              ${formatAndDropDecimal(totalCashDue)} cash down
            </div>
            <Clickable
              data-analytics-id={null}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                sendOpenEvent(OpenSource.Terms);
                setMoreDetailsModalOpen(true, {
                  openTo: MoreDetailsModalSections.PriceDetails,
                  vehicleId: tileData.vehicleId
                });
              }}
              className="flex self-center"
              element="span"
            >
              <InfoIcon />
            </Clickable>
          </div>
        </>
      )}
    </div>
  );
}
